/**
 * v1.3.82 — SQLite-backed playlist cache (native only).
 *
 * WHY: On a 590k-channel Xtream playlist IndexedDB spends ~4-8 seconds
 * doing cursor walks + structured-clone deserialisation on every cold
 * start of Browse.jsx (per-category tab switch). SQLite lets us push
 * the WHERE clause down to a C engine and get the same rows back in
 * ~150 ms with a proper B-tree index. That's the single biggest
 * bottleneck low-RAM Smart TVs hit today.
 *
 * DESIGN
 * ------
 *   • Additive, not destructive. IDB (`playlistCache.js`) stays as the
 *     source of truth. This module is a READ-THROUGH cache layered on
 *     top: writes duplicate to SQL, reads prefer SQL, fall back to IDB
 *     when SQL is empty (and opportunistically hydrate SQL on the way).
 *   • Available on Capacitor Android + iOS + Electron. Web falls back
 *     to `jeep-sqlite` (WASM) OR just returns "unavailable" — the
 *     dispatcher handles both.
 *   • One shared connection, opened lazily on first call. Errors are
 *     swallowed and the module reports "not available" so the caller
 *     transparently drops back to IDB.
 *
 * SCHEMA
 * ------
 *   channels(playlist_id, channel_id, content_type, group_name,
 *            name, stream_url, logo_url, tvg_id, data)
 *      PK (playlist_id, channel_id)
 *      IX (playlist_id, content_type, group_name)
 *
 *   epg(playlist_id, tvg_id, programmes)
 *      PK (playlist_id, tvg_id)
 *
 *   kv(scope, k, fetched_at, data)     -- series_eps / vod_cats /
 *      PK (scope, k)                    -- vod_streams / live_cats /
 *                                       -- live_streams / short_epg
 *
 *   meta(playlist_id, data, fetched_at)
 *      PK (playlist_id)
 */
import { Capacitor } from "@capacitor/core";
import { CapacitorSQLite, SQLiteConnection } from "@capacitor-community/sqlite";

const DB_NAME = "za_player_pro_v1382";
const SCHEMA_VERSION = 2;

// One CapacitorSQLite wrapper, opened once.
const _sqlite = typeof CapacitorSQLite !== "undefined"
  ? new SQLiteConnection(CapacitorSQLite)
  : null;

let _dbPromise = null;
let _lastError = null;

/**
 * Idempotent — safe to call from every entry point. Resolves to the
 * open connection or `null` if the plugin isn't wired in (web build
 * without jeep-sqlite, plugin failed to init, etc.).
 */
async function _openDb() {
  if (_dbPromise) return _dbPromise;
  if (!_sqlite) return null;

  _dbPromise = (async () => {
    try {
      // Native (Android / iOS / Electron): the plugin is autolinked
      // and ready. Web needs an extra `jeep-sqlite` bootstrap step
      // (initWebStore) — we skip web entirely because IDB is already
      // fast enough there.
      const platform = Capacitor.getPlatform();
      if (platform === "web") {
        return null;
      }

      // v1.4.28-j — ROOT-CAUSE FIX for the recurring
      //   "Connection za_player_pro_v1382 does not exist"
      // warning that fired on EVERY cold start (per device logs
      // 2026-07-13). The Capacitor SQLite plugin keeps its own
      // native-side connection map that persists across WebView
      // reloads on Android. Result: after any hot-reload / cold
      // start the plugin thinks the DB is still open but the JS
      // wrapper's Map is empty → createConnection throws "already
      // exists" AND retrieveConnection throws "does not exist" —
      // the classic double-fault state.
      //
      // The `checkConnectionsConsistency([DB_NAME], readonly=false)`
      // API is the OFFICIAL fix from the plugin author for exactly
      // this pattern — it reconciles the native map with the JS
      // wrapper's Map and prunes stale entries on both sides. Once
      // reconciled, `createConnection` succeeds first-try and the
      // warning stops firing on subsequent app opens.
      //
      // We wrap in a try because on plugin versions older than
      // 6.0.0 the API returns void / undefined and the shape check
      // below defends against that.
      try {
        await _sqlite.checkConnectionsConsistency([DB_NAME], false);
      } catch (consistencyErr) {
        // Not fatal — the recovery block below still catches the
        // double-fault. Just log so we notice if the API breaks
        // in a future plugin release.
        // eslint-disable-next-line no-console
        console.warn(
          "[playlistCacheSql] checkConnectionsConsistency failed (non-fatal):",
          consistencyErr?.message || consistencyErr,
        );
      }

      // Check whether the connection is already open (survives HMR
      // during dev). isConnection(readonly=false).
      const isConn = await _sqlite.isConnection(DB_NAME, false);
      let db;
      if (isConn?.result) {
        db = await _sqlite.retrieveConnection(DB_NAME, false);
      } else {
        // Belt-and-braces: even after consistency-check the plugin
        // occasionally still throws on the first createConnection
        // on very cold devices (Chromecast HD, first-gen Fire TV).
        // Keep the reset ladder as a fallback — but it should now
        // be dead code on 99 % of boots.
        try {
          db = await _sqlite.createConnection(
            DB_NAME, false, "no-encryption", SCHEMA_VERSION, false,
          );
        } catch (createErr) {
          const msg = String(createErr?.message || createErr || "");
          if (/already exists/i.test(msg)) {
            try {
              db = await _sqlite.retrieveConnection(DB_NAME, false);
            } catch (retrieveErr) {
              // eslint-disable-next-line no-console
              console.warn(
                `[playlistCacheSql] both create+retrieve failed (${retrieveErr?.message || retrieveErr}) — running full plugin reset via closeConnection`,
              );
              try {
                await _sqlite.closeConnection(DB_NAME, false);
              } catch { /* the plugin's map may have been empty — safe to ignore */ }
              db = await _sqlite.createConnection(
                DB_NAME, false, "no-encryption", SCHEMA_VERSION, false,
              );
            }
          } else {
            throw createErr;
          }
        }
      }
      await db.open();
      await db.execute(`
        CREATE TABLE IF NOT EXISTS channels (
          playlist_id  TEXT NOT NULL,
          channel_id   TEXT NOT NULL,
          content_type TEXT NOT NULL,
          group_name   TEXT NOT NULL,
          name         TEXT,
          stream_url   TEXT,
          logo_url     TEXT,
          tvg_id       TEXT,
          data         TEXT NOT NULL,
          PRIMARY KEY (playlist_id, channel_id)
        );
        CREATE INDEX IF NOT EXISTS idx_channels_pl_ct_gr
          ON channels(playlist_id, content_type, group_name);
        CREATE INDEX IF NOT EXISTS idx_channels_pl_ct
          ON channels(playlist_id, content_type);

        CREATE TABLE IF NOT EXISTS epg (
          playlist_id TEXT NOT NULL,
          tvg_id      TEXT NOT NULL,
          programmes  TEXT NOT NULL,
          PRIMARY KEY (playlist_id, tvg_id)
        );

        CREATE TABLE IF NOT EXISTS meta (
          playlist_id TEXT PRIMARY KEY,
          data        TEXT NOT NULL,
          fetched_at  INTEGER
        );

        CREATE TABLE IF NOT EXISTS kv (
          scope      TEXT NOT NULL,
          k          TEXT NOT NULL,
          fetched_at INTEGER,
          data       TEXT NOT NULL,
          PRIMARY KEY (scope, k)
        );

        -- v1.3.0 — Pre-aggregated series tiles. One row per SHOW (not per
        -- episode), computed during playlist parse. Kills the ~1s freeze
        -- on Series category clicks caused by the 10k-episode JS Map walk.
        CREATE TABLE IF NOT EXISTS series_tiles (
          playlist_id  TEXT NOT NULL,
          series_key   TEXT NOT NULL,
          series_name  TEXT NOT NULL,
          group_name   TEXT,
          poster_url   TEXT,
          year         INTEGER,
          episode_count INTEGER,
          last_added_ts INTEGER,
          series_id    TEXT,
          tmdb_id      INTEGER,
          first_order  INTEGER,
          first_ep_url TEXT,
          data         TEXT NOT NULL,
          PRIMARY KEY (playlist_id, series_key)
        );
        CREATE INDEX IF NOT EXISTS idx_series_tiles_pl_grp
          ON series_tiles(playlist_id, group_name);
        CREATE INDEX IF NOT EXISTS idx_series_tiles_pl_recent
          ON series_tiles(playlist_id, last_added_ts DESC);
        CREATE INDEX IF NOT EXISTS idx_series_tiles_missing_poster
          ON series_tiles(playlist_id) WHERE poster_url IS NULL;

        -- v1.3.0 — Pre-resolved movie tiles. poster_url is the winning
        -- URL from the resolution chain (stream_icon > cover > logo). No
        -- lazy TMDB waterfall on scroll.
        CREATE TABLE IF NOT EXISTS movie_tiles (
          playlist_id  TEXT NOT NULL,
          stream_url   TEXT NOT NULL,
          name         TEXT,
          group_name   TEXT,
          poster_url   TEXT,
          year         INTEGER,
          rating       REAL,
          duration_s   INTEGER,
          added_ts     INTEGER,
          first_order  INTEGER,
          data         TEXT NOT NULL,
          PRIMARY KEY (playlist_id, stream_url)
        );
        CREATE INDEX IF NOT EXISTS idx_movie_tiles_pl_grp
          ON movie_tiles(playlist_id, group_name);
      `);
      return db;
    } catch (e) {
      _lastError = e?.message || String(e);
      // eslint-disable-next-line no-console
      console.warn("[playlistCacheSql] open failed — falling back to IDB:", _lastError);
      return null;
    }
  })();
  return _dbPromise;
}

/**
 * Cheap sync check callers can use to short-circuit before awaiting.
 * Returns true only AFTER `_openDb` has successfully resolved at
 * least once during this session.
 */
let _isAvailable = false;
export function sqlIsAvailable() {
  return _isAvailable;
}

/**
 * Kick off the open. Idempotent. Callers that want to await
 * "SQL is ready OR gave up trying" should `await ensureReady()`.
 */
export async function ensureReady() {
  const db = await _openDb();
  _isAvailable = !!db;
  return _isAvailable;
}

export function lastError() {
  return _lastError;
}

// ---------------------------------------------------------------
// Channels
// ---------------------------------------------------------------

/**
 * Replace all channel rows for one playlist. Idempotent + atomic
 * (single transaction). Called from `idbPutPlaylist` in `playlistCache.js`
 * during the normal write-through path.
 */
export async function sqlPutChannels(playlistId, channels) {
  if (!playlistId || !Array.isArray(channels)) return 0;
  const db = await _openDb();
  if (!db) return 0;

  // Batch inserts of ~500 rows/transaction. The SQLite plugin's
  // executeSet API takes an array of statements and runs them under
  // one transaction — 100× faster than one exec-per-row on 500k
  // catalogs.
  const BATCH = 500;
  try {
    await db.execute(`DELETE FROM channels WHERE playlist_id='${_esc(playlistId)}';`);
    for (let i = 0; i < channels.length; i += BATCH) {
      const slice = channels.slice(i, i + BATCH);
      const set = slice.map((c) => ({
        statement: `INSERT OR REPLACE INTO channels
          (playlist_id, channel_id, content_type, group_name, name,
           stream_url, logo_url, tvg_id, data)
          VALUES (?,?,?,?,?,?,?,?,?);`,
        values: [
          playlistId,
          String(c.id || c.stream_url || ""),
          String(c.content_type || ""),
          String(c.group || ""),
          c.name || null,
          c.stream_url || null,
          c.logo_url || null,
          c.tvg_id || null,
          JSON.stringify(c),
        ],
      }));
      await db.executeSet(set, /* transaction */ true);
    }
    // v1.3.0 — Fire tile pre-aggregation in the same call so the two
    // tables stay consistent with `channels`. Fire-and-forget: even if
    // tiles fail to build, `channels` is still queryable.
    try { await sqlBuildTiles(playlistId, channels); } catch { /* noop */ }
    return channels.length;
  } catch (e) {
    _lastError = e?.message || String(e);
    return 0;
  }
}

/**
 * v1.3.0 — Pre-aggregate series + movie tiles into their own tables.
 * Runs during playlist parse (called from `sqlPutChannels`). Cost is
 * ~100ms for 500k rows, paid ONCE at import — savings are ~1s per
 * Series category click at runtime because the runtime path becomes
 * a raw indexed SELECT instead of a JS Map walk.
 *
 * Poster resolution priority per tile:
 *   1. explicit `stream_icon` / `cover` / `cover_big`
 *   2. `logo_url` (tvg-logo from the M3U)
 *   3. NULL — background TMDB harvest fills this in later
 */
export async function sqlBuildTiles(playlistId, channels) {
  if (!playlistId || !Array.isArray(channels)) return { series: 0, movies: 0 };
  const db = await _openDb();
  if (!db) return { series: 0, movies: 0 };

  // ---- Series aggregation (JS Map — one pass, hot loop) ----
  // v1.3.1 — Key each row by (series_key, group_name) so a show that
  // appears in TWO M3U groups (e.g. "Comedy" AND "Recently episodes
  // added") gets a row for each group. Otherwise the second group's
  // SELECT returns empty and the UI shows "Select a category" even
  // though the show has episodes there.
  const seriesMap = new Map();
  const movieRows = [];
  for (const ch of channels) {
    const ct = ch.content_type || "";
    if (ct === "series") {
      const key = ch.series_name || ch.name || "";
      if (!key) continue;
      const skey =
        (ch.xc_kind === "series" && ch.xc_id && `xc:${ch.xc_id}`) ||
        `nm:${String(key).toLowerCase().trim()}`;
      const groupName = ch.group || "";
      const mapKey = `${skey}||${groupName}`;
      const poster =
        ch.cover_big || ch.cover || ch.stream_icon || ch.logo_url || null;
      const addedTs =
        Number(ch.series_last_modified_ts || ch.added_ts || 0) || 0;
      const order = Number(ch.order || 0) || 0;
      let tile = seriesMap.get(mapKey);
      if (!tile) {
        tile = {
          playlist_id: playlistId,
          series_key: `${skey}#${groupName}`,
          series_name: key,
          group_name: groupName || null,
          poster_url: poster,
          year: ch.year || null,
          episode_count: 0,
          last_added_ts: addedTs,
          series_id:
            ch.xc_kind === "series" && ch.xc_id ? String(ch.xc_id) : null,
          tmdb_id: ch.tmdb_id || null,
          first_order: order,
          first_ep_url: ch.stream_url || null,
        };
        seriesMap.set(mapKey, tile);
      } else {
        if (!tile.poster_url && poster) tile.poster_url = poster;
        if (!tile.series_id && ch.xc_kind === "series" && ch.xc_id) {
          tile.series_id = String(ch.xc_id);
        }
        if (!tile.tmdb_id && ch.tmdb_id) tile.tmdb_id = ch.tmdb_id;
        if (order && (!tile.first_order || order < tile.first_order)) {
          tile.first_order = order;
          tile.first_ep_url = ch.stream_url || tile.first_ep_url;
        }
        if (addedTs > tile.last_added_ts) tile.last_added_ts = addedTs;
        if ((ch.year || 0) > (tile.year || 0)) tile.year = ch.year;
      }
      tile.episode_count += 1;
    } else if (ct === "movie") {
      movieRows.push({
        playlist_id: playlistId,
        stream_url: ch.stream_url || "",
        name: ch.name || null,
        group_name: ch.group || null,
        poster_url:
          ch.cover_big || ch.cover || ch.stream_icon || ch.logo_url || null,
        year: ch.year || null,
        rating: typeof ch.rating === "number" ? ch.rating : null,
        duration_s: ch.duration_s || null,
        added_ts: Number(ch.added_ts || 0) || null,
        first_order: Number(ch.order || 0) || null,
      });
    }
  }

  // ---- Bulk write series ----
  const BATCH = 500;
  try {
    await db.execute(
      `DELETE FROM series_tiles WHERE playlist_id='${_esc(playlistId)}';`,
    );
    const seriesArr = Array.from(seriesMap.values());
    for (let i = 0; i < seriesArr.length; i += BATCH) {
      const slice = seriesArr.slice(i, i + BATCH);
      const set = slice.map((t) => ({
        statement: `INSERT OR REPLACE INTO series_tiles
          (playlist_id, series_key, series_name, group_name, poster_url,
           year, episode_count, last_added_ts, series_id, tmdb_id,
           first_order, first_ep_url, data)
          VALUES (?,?,?,?,?,?,?,?,?,?,?,?,?);`,
        values: [
          t.playlist_id,
          t.series_key,
          t.series_name,
          t.group_name,
          t.poster_url,
          t.year,
          t.episode_count,
          t.last_added_ts,
          t.series_id,
          t.tmdb_id,
          t.first_order,
          t.first_ep_url,
          JSON.stringify(t),
        ],
      }));
      await db.executeSet(set, true);
    }
  } catch (e) {
    _lastError = e?.message || String(e);
  }

  // ---- Bulk write movies ----
  try {
    await db.execute(
      `DELETE FROM movie_tiles WHERE playlist_id='${_esc(playlistId)}';`,
    );
    for (let i = 0; i < movieRows.length; i += BATCH) {
      const slice = movieRows.slice(i, i + BATCH);
      const set = slice.map((m) => ({
        statement: `INSERT OR REPLACE INTO movie_tiles
          (playlist_id, stream_url, name, group_name, poster_url,
           year, rating, duration_s, added_ts, first_order, data)
          VALUES (?,?,?,?,?,?,?,?,?,?,?);`,
        values: [
          m.playlist_id,
          m.stream_url,
          m.name,
          m.group_name,
          m.poster_url,
          m.year,
          m.rating,
          m.duration_s,
          m.added_ts,
          m.first_order,
          JSON.stringify(m),
        ],
      }));
      await db.executeSet(set, true);
    }
  } catch (e) {
    _lastError = e?.message || String(e);
  }

  return { series: seriesMap.size, movies: movieRows.length };
}

/**
 * v1.3.0 — Read series tiles for a category. Returns them ordered so
 * the sidebar's "Recently episodes added" pseudo-group works the same
 * as before (newest first). Callers pass `group="__all__"` to get
 * every tile in the playlist.
 */
export async function sqlGetSeriesTiles(playlistId, group) {
  const db = await _openDb();
  if (!db) return null;
  try {
    let q, args;
    if (!group || group === "__all__") {
      q = `SELECT data FROM series_tiles
             WHERE playlist_id=?
             ORDER BY first_order ASC;`;
      args = [playlistId];
    } else if (group === "__recent__") {
      q = `SELECT data FROM series_tiles
             WHERE playlist_id=?
             ORDER BY last_added_ts DESC
             LIMIT 500;`;
      args = [playlistId];
    } else {
      q = `SELECT data FROM series_tiles
             WHERE playlist_id=? AND group_name=?
             ORDER BY first_order ASC;`;
      args = [playlistId, String(group)];
    }
    const r = await db.query(q, args);
    return (r?.values || []).map((row) => JSON.parse(row.data));
  } catch (e) {
    _lastError = e?.message || String(e);
    return null;
  }
}

/** v1.3.0 — Read movie tiles for a category. Same pattern. */
export async function sqlGetMovieTiles(playlistId, group) {
  const db = await _openDb();
  if (!db) return null;
  try {
    let q, args;
    if (!group || group === "__all__") {
      q = `SELECT data FROM movie_tiles
             WHERE playlist_id=?
             ORDER BY first_order ASC;`;
      args = [playlistId];
    } else {
      q = `SELECT data FROM movie_tiles
             WHERE playlist_id=? AND group_name=?
             ORDER BY first_order ASC;`;
      args = [playlistId, String(group)];
    }
    const r = await db.query(q, args);
    return (r?.values || []).map((row) => JSON.parse(row.data));
  } catch (e) {
    _lastError = e?.message || String(e);
    return null;
  }
}

/**
 * v1.3.0 — Distinct series category names + counts, straight from the
 * pre-aggregated `series_tiles` table. Cheaper than counting distinct
 * shows out of the raw `channels` table on every render.
 */
export async function sqlGetSeriesCategories(playlistId) {
  const db = await _openDb();
  if (!db) return null;
  try {
    const r = await db.query(
      `SELECT group_name AS group_name, COUNT(*) AS count
         FROM series_tiles
        WHERE playlist_id=?
        GROUP BY group_name
        ORDER BY group_name;`,
      [playlistId],
    );
    return (r?.values || []).map((row) => ({
      group: row.group_name || "",
      count: row.count | 0,
    }));
  } catch (e) {
    _lastError = e?.message || String(e);
    return null;
  }
}

/**
 * v1.3.0 — Patch a resolved TMDB poster back onto a series tile.
 * Called from the background TMDB warm loop so subsequent reads see
 * the resolved URL without waiting for the network on scroll.
 *
 * v1.3.1 — Because one show can now live in multiple group_name rows
 * (see aggregation change), we match by series_name so the resolved
 * poster fills EVERY copy in a single UPDATE. Prevents blank tiles
 * in secondary groups like "Recently episodes added".
 */
export async function sqlUpdateSeriesPoster(playlistId, seriesKey, posterUrl, seriesName) {
  if (!playlistId || !posterUrl) return false;
  const db = await _openDb();
  if (!db) return false;
  try {
    if (seriesName) {
      await db.run(
        `UPDATE series_tiles
            SET poster_url=?
          WHERE playlist_id=?
            AND series_name=?
            AND (poster_url IS NULL OR poster_url='');`,
        [posterUrl, playlistId, seriesName],
      );
    } else if (seriesKey) {
      await db.run(
        `UPDATE series_tiles SET poster_url=? WHERE playlist_id=? AND series_key=?;`,
        [posterUrl, playlistId, seriesKey],
      );
    }
    return true;
  } catch (e) {
    _lastError = e?.message || String(e);
    return false;
  }
}

/** v1.3.0 — Return series tiles missing a poster so the background
 *  TMDB warmer can pick them up. Capped so we never queue 50k
 *  outstanding requests at once. */
export async function sqlGetSeriesMissingPoster(playlistId, limit = 200) {
  const db = await _openDb();
  if (!db) return null;
  try {
    const r = await db.query(
      `SELECT series_key, series_name, year
         FROM series_tiles
        WHERE playlist_id=? AND (poster_url IS NULL OR poster_url='')
        LIMIT ?;`,
      [playlistId, limit],
    );
    return (r?.values || []).map((row) => ({
      series_key: row.series_key,
      series_name: row.series_name,
      year: row.year || null,
    }));
  } catch (e) {
    _lastError = e?.message || String(e);
    return null;
  }
}

/** Return every channel row for one playlist (used by the
 *  full-playlist reassembly path in loadPlaylist). */
export async function sqlGetAllChannels(playlistId) {
  const db = await _openDb();
  if (!db) return null;
  try {
    const r = await db.query(
      `SELECT data FROM channels WHERE playlist_id=?;`,
      [playlistId],
    );
    return (r?.values || []).map((row) => JSON.parse(row.data));
  } catch (e) {
    _lastError = e?.message || String(e);
    return null;
  }
}

/**
 * Fast per-category loader — the reason we bothered migrating to SQL.
 * A 500k-channel catalog's "Action Movies" bucket returns in ~30 ms
 * from SQL vs ~800 ms from an IDB cursor walk.
 */
export async function sqlGetChannelsByGroup(playlistId, contentType, group) {
  const db = await _openDb();
  if (!db) return null;
  try {
    const r = await db.query(
      `SELECT data FROM channels
        WHERE playlist_id=? AND content_type=? AND group_name=?;`,
      [playlistId, String(contentType || ""), String(group || "")],
    );
    return (r?.values || []).map((row) => JSON.parse(row.data));
  } catch (e) {
    _lastError = e?.message || String(e);
    return null;
  }
}

/**
 * Distinct group names + counts for one (playlist, content_type).
 * SQL does the GROUP BY natively — no loading rows into JS heap
 * just to count them.
 */
export async function sqlGetCategoriesForType(playlistId, contentType) {
  const db = await _openDb();
  if (!db) return null;
  try {
    const r = await db.query(
      `SELECT group_name AS group_name, COUNT(*) AS count
         FROM channels
        WHERE playlist_id=? AND content_type=?
        GROUP BY group_name
        ORDER BY group_name;`,
      [playlistId, String(contentType || "")],
    );
    return (r?.values || []).map((row) => ({
      group: row.group_name || "",
      count: row.count | 0,
    }));
  } catch (e) {
    _lastError = e?.message || String(e);
    return null;
  }
}

// ---------------------------------------------------------------
// EPG
// ---------------------------------------------------------------

export async function sqlPutEpg(playlistId, epgByChannel) {
  if (!playlistId || !epgByChannel) return 0;
  const db = await _openDb();
  if (!db) return 0;
  try {
    await db.execute(`DELETE FROM epg WHERE playlist_id='${_esc(playlistId)}';`);
    const keys = Object.keys(epgByChannel);
    const BATCH = 500;
    for (let i = 0; i < keys.length; i += BATCH) {
      const slice = keys.slice(i, i + BATCH);
      const set = slice.map((tvg) => ({
        statement: `INSERT OR REPLACE INTO epg (playlist_id, tvg_id, programmes)
                    VALUES (?,?,?);`,
        values: [playlistId, tvg, JSON.stringify(epgByChannel[tvg])],
      }));
      await db.executeSet(set, true);
    }
    return keys.length;
  } catch (e) {
    _lastError = e?.message || String(e);
    return 0;
  }
}

export async function sqlGetEpg(playlistId) {
  const db = await _openDb();
  if (!db) return null;
  try {
    const r = await db.query(
      `SELECT tvg_id, programmes FROM epg WHERE playlist_id=?;`,
      [playlistId],
    );
    const out = {};
    for (const row of (r?.values || [])) {
      try { out[row.tvg_id] = JSON.parse(row.programmes); } catch { /* skip */ }
    }
    return out;
  } catch (e) {
    _lastError = e?.message || String(e);
    return null;
  }
}

// ---------------------------------------------------------------
// Meta
// ---------------------------------------------------------------

export async function sqlPutMeta(playlistId, meta) {
  if (!playlistId || !meta) return false;
  const db = await _openDb();
  if (!db) return false;
  try {
    await db.run(
      `INSERT OR REPLACE INTO meta (playlist_id, data, fetched_at)
       VALUES (?,?,?);`,
      [playlistId, JSON.stringify(meta), meta.fetched_at || Date.now()],
    );
    return true;
  } catch (e) {
    _lastError = e?.message || String(e);
    return false;
  }
}

export async function sqlGetMeta(playlistId) {
  const db = await _openDb();
  if (!db) return null;
  try {
    const r = await db.query(
      `SELECT data FROM meta WHERE playlist_id=? LIMIT 1;`,
      [playlistId],
    );
    const row = (r?.values || [])[0];
    if (!row) return null;
    return JSON.parse(row.data);
  } catch (e) {
    _lastError = e?.message || String(e);
    return null;
  }
}

// ---------------------------------------------------------------
// KV (series_eps / vod_cats / vod_streams / live_cats /
//     live_streams / short_epg — small blobs, key-value access)
// ---------------------------------------------------------------

export async function sqlKvGet(scope, k) {
  const db = await _openDb();
  if (!db) return null;
  try {
    const r = await db.query(
      `SELECT fetched_at, data FROM kv WHERE scope=? AND k=? LIMIT 1;`,
      [scope, String(k)],
    );
    const row = (r?.values || [])[0];
    if (!row) return null;
    return { fetched_at: row.fetched_at | 0, data: JSON.parse(row.data) };
  } catch (e) {
    _lastError = e?.message || String(e);
    return null;
  }
}

export async function sqlKvPut(scope, k, data) {
  const db = await _openDb();
  if (!db) return false;
  try {
    await db.run(
      `INSERT OR REPLACE INTO kv (scope, k, fetched_at, data)
       VALUES (?,?,?,?);`,
      [scope, String(k), Date.now(), JSON.stringify(data)],
    );
    return true;
  } catch (e) {
    _lastError = e?.message || String(e);
    return false;
  }
}

// ---------------------------------------------------------------
// Whole-playlist delete
// ---------------------------------------------------------------

export async function sqlDeletePlaylist(playlistId) {
  const db = await _openDb();
  if (!db) return false;
  try {
    const esc = _esc(playlistId);
    // Also purge kv rows scoped to this playlist. kv keys are of the
    // shape `${playlistId}` or `${playlistId}:...` — SQL LIKE covers
    // both.
    await db.execute(`
      DELETE FROM channels WHERE playlist_id='${esc}';
      DELETE FROM epg      WHERE playlist_id='${esc}';
      DELETE FROM meta     WHERE playlist_id='${esc}';
      DELETE FROM kv       WHERE k='${esc}' OR k LIKE '${esc}:%';
    `);
    return true;
  } catch (e) {
    _lastError = e?.message || String(e);
    return false;
  }
}

/**
 * v1.3.82 — Truncate every SQL table without dropping the schema.
 * Invoked by `clearAllPlaylistCaches()` in playlistCache.js so a
 * "Clear cache" button in Settings wipes both backends symmetrically.
 */
export async function sqlClearAll() {
  const db = await _openDb();
  if (!db) return false;
  try {
    await db.execute(`
      DELETE FROM channels;
      DELETE FROM epg;
      DELETE FROM meta;
      DELETE FROM kv;
    `);
    return true;
  } catch (e) {
    _lastError = e?.message || String(e);
    return false;
  }
}

// ---------------------------------------------------------------
// Diagnostics (Admin/Settings surfaces these on the Cache Health card).
// ---------------------------------------------------------------

export async function sqlStats() {
  const db = await _openDb();
  if (!db) {
    return { available: false, error: _lastError };
  }
  try {
    const channels = await db.query(`SELECT COUNT(*) AS n FROM channels;`);
    const epg = await db.query(`SELECT COUNT(*) AS n FROM epg;`);
    const meta = await db.query(`SELECT COUNT(*) AS n FROM meta;`);
    const kv = await db.query(`SELECT COUNT(*) AS n FROM kv;`);
    return {
      available: true,
      channels: channels?.values?.[0]?.n | 0,
      epg: epg?.values?.[0]?.n | 0,
      meta: meta?.values?.[0]?.n | 0,
      kv: kv?.values?.[0]?.n | 0,
    };
  } catch (e) {
    return { available: false, error: e?.message || String(e) };
  }
}

// Basic SQL-string escape for the DDL DELETE paths (parameter binding
// via `run()` handles the value paths). Only used with playlist ids
// which are UUIDs from the backend, so this is defense-in-depth.
function _esc(s) {
  return String(s).replace(/'/g, "''");
}
